import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InfoIcon from '@mui/icons-material/Info';
import GroupIcon from '@mui/icons-material/Group';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { listFeed } from '../../api/runs';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { FeedEvent, FeedEventType } from '../../api/types';
import { feedMessage } from '../../utils/feedMessage';
import { formatRelativeTime } from '../../utils/relativeTime';
import { useWebSocket } from '../../hooks/useWebSocket';
import CountdownTimer from '../../components/CountdownTimer';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingSpinner from '../../components/LoadingSpinner';

type ChipColor = 'default' | 'primary' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'error' | 'info';

const TYPE_CONFIG: Record<
  FeedEventType,
  { icon: React.ReactNode; color: ChipColor; accentVar: string }
> = {
  // Coloured per the design's pill-chip palette: yellow announcements,
  // indigo system, green milestones, warning countdowns, red alerts.
  announcement: { icon: <CampaignIcon />, color: 'secondary', accentVar: 'secondary.main' },
  system: { icon: <InfoIcon />, color: 'primary', accentVar: 'primary.main' },
  milestone: { icon: <EmojiEventsIcon />, color: 'tertiary', accentVar: 'tertiary.main' },
  countdown: { icon: <AccessTimeIcon />, color: 'warning', accentVar: 'warning.main' },
  reminder: { icon: <CampaignIcon />, color: 'primary', accentVar: 'primary.main' },
  alert: { icon: <NotificationImportantIcon />, color: 'error', accentVar: 'error.main' },
  team_update: { icon: <GroupIcon />, color: 'primary', accentVar: 'primary.main' },
  results_available: { icon: <EmojiEventsIcon />, color: 'tertiary', accentVar: 'tertiary.main' },
  feedback_prompt: { icon: <RateReviewIcon />, color: 'secondary', accentVar: 'secondary.main' },
};

function targetTimeOf(ev: FeedEvent): string | undefined {
  const c = ev.content as { target_time?: string };
  return c.target_time;
}

const FeedView: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const { user } = useAuth();
  const { t, i18n } = useTranslation('runs');
  const navigate = useNavigate();

  const orgSlug = user?.organization_slug;

  const [events, setEvents] = useState<FeedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const scrolledOnLoad = useRef(false);

  // Re-render once a minute so relative timestamps stay fresh.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const formatRelative = useCallback(
    (iso: string): string => formatRelativeTime(iso, now, t),
    [now, t],
  );

  const load = useCallback(async () => {
    if (!orgSlug || !runId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await listFeed(orgSlug, runId);
      setEvents(resp.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, runId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Events render chronologically (oldest first, per the API contract) — a
  // participant joining mid-run cares about the newest entry at the bottom,
  // not the top of the run's history, so scroll there once after the initial
  // load (not on every WS event, so reading old entries isn't interrupted).
  useEffect(() => {
    if (!loading && !scrolledOnLoad.current) {
      scrolledOnLoad.current = true;
      bottomRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [loading]);

  useWebSocket({
    path: orgSlug && runId ? `/api/orgs/${orgSlug}/runs/${runId}/ws` : '',
    enabled: !!(orgSlug && runId),
    handlers: {
      feed_event: (data) => {
        const ev = data as FeedEvent;
        setEvents((prev) => {
          if (prev.some((e) => e.id === ev.id)) return prev;
          // Newest last, matching the chronological (oldest-first) order the
          // initial GET returns — a prepend here would put a brand-new live
          // event above the run's oldest history.
          return [...prev, ev];
        });
      },
    },
  });

  if (loading) return <LoadingSpinner />;

  return (
    <Stack spacing={2} aria-live="polite" aria-atomic={false}>
      <Typography variant="h2" component="h1">
        {t('participant.feed.title')}
      </Typography>

      {error && <ErrorAlert message={error} onRetry={load} />}

      {events.length === 0 && !error && (
        <Card sx={{ textAlign: 'center', py: 5 }}>
          <CardContent>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <CampaignIcon fontSize="large" />
            </Box>
            <Typography variant="h5" component="p" gutterBottom>
              {t('participant.feed.emptyTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('participant.feed.emptySubtitle')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {events.map((ev) => {
        const cfg = TYPE_CONFIG[ev.event_type] ?? TYPE_CONFIG.system;
        const msg = feedMessage(ev.content, i18n.language);
        const target = targetTimeOf(ev);

        return (
          <Card
            key={ev.id}
            variant="outlined"
            role={ev.event_type === 'alert' ? 'alert' : undefined}
            sx={{
              borderLeftWidth: 4,
              borderLeftColor: cfg.accentVar,
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                <Chip
                  icon={cfg.icon as React.ReactElement}
                  label={t(`feedComposer.types.${ev.event_type}`)}
                  size="small"
                  color={cfg.color}
                  sx={{ fontWeight: 600 }}
                />
                <Box sx={{ flex: 1 }} />
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                  <AccessTimeIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption">
                    {formatRelative(ev.created_at)}
                  </Typography>
                </Stack>
              </Stack>

              {msg && (
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {msg}
                </Typography>
              )}

              {ev.event_type === 'countdown' && target && (
                <Box sx={{ mt: 1 }}>
                  <CountdownTimer targetTime={target} />
                </Box>
              )}

              {/* Phase 2.4 / 5.1b — the end-of-run FEEDBACK_PROMPT feed event
                  is the participant's entry point into the feedback form (the
                  hard end-of-run redirect is dormant, see #242). Submission is
                  anonymous on the backend, so this works even after the guest
                  account was cleaned up when the run ended. */}
              {ev.event_type === 'feedback_prompt' && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<RateReviewIcon />}
                    onClick={() => navigate(`/runs/${runId}/participant/feedback`)}
                  >
                    {t('participant.feed.feedbackCta')}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })}
      <div ref={bottomRef} />
    </Stack>
  );
};

export default FeedView;
